'use client';

import { Alert, type AlertProps, Box, Snackbar, type SnackbarOrigin, type SnackbarProps } from '@mui/material';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { BackOfficeConfig, CustomComponent } from '../types/main';

interface AppProps {
  location?: string;
  baseRoute: string;
  config?: BackOfficeConfig;
  customComponents?: CustomComponent[];
}
interface GlobalProviderProps extends AppProps {
  children: React.ReactNode;
}

type SnackbarSeverity = Extract<AlertProps['severity'], 'success' | 'error' | 'warning'>;

interface ShowSnackbarProps {
  message: SnackbarProps['message'];
  severity?: SnackbarSeverity;
  autoHideDuration?: SnackbarProps['autoHideDuration'];
  anchorOrigin?: SnackbarOrigin;
}

interface ShowAlertProps {
  message: React.ReactNode;
}

interface GlobalContextType {
  appProps: AppProps;
  customComponentsMap: Record<string, React.ComponentType<Record<string, unknown>>>;
  showAlert: (props: ShowAlertProps) => void;
  showSnackbar: (props: ShowSnackbarProps) => void;
}

const DEFAULT_AUTO_HIDE_DURATION = 5000;
const DEFAULT_SEVERITY: SnackbarSeverity = 'success';
const DEFAULT_SNACKBAR_POSITION = {
  vertical: 'bottom',
  horizontal: 'center',
} satisfies SnackbarOrigin;

const GlobalContext = createContext<GlobalContextType>(undefined!);

export const GlobalProvider = ({
  children,
  customComponents,
  ...appProps
}: GlobalProviderProps) => {
  const [customComponentsMap, setCustomComponentsMap] = useState<
    Record<string, React.ComponentType<Record<string, unknown>>>
  >({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarProps, setSnackbarProps] = useState<ShowSnackbarProps>();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<React.ReactNode>('');

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const handleAlertClose = useCallback(() => {
    setAlertOpen(false);
  }, []);

  const showSnackbar = useCallback(
    ({
      message,
      severity = DEFAULT_SEVERITY,
      autoHideDuration,
      anchorOrigin = DEFAULT_SNACKBAR_POSITION,
    }: ShowSnackbarProps) => {
      const resolvedAutoHide =
        autoHideDuration !== undefined
          ? autoHideDuration
          : severity === 'error'
            ? null
            : DEFAULT_AUTO_HIDE_DURATION;

      setSnackbarProps({
        message,
        severity,
        autoHideDuration: resolvedAutoHide,
        anchorOrigin,
      });
      setSnackbarOpen(true);
    },
    [],
  );

  const showAlert = useCallback(({ message }: ShowAlertProps) => {
    setAlertMessage(message);
    setAlertOpen(true);
  }, []);

  useEffect(() => {
    if (!alertOpen) return undefined;
    const timeoutId = window.setTimeout(() => {
      setAlertOpen(false);
    }, DEFAULT_AUTO_HIDE_DURATION);
    return () => window.clearTimeout(timeoutId);
  }, [alertOpen, alertMessage]);

  useEffect(() => {
    const loadComponents = async () => {
      const loadedComponents: Record<string, React.ComponentType<Record<string, unknown>>> = {};

      if (Array.isArray(customComponents)) {
        await Promise.all(
          customComponents.map(async (component) => {
            try {
              const loadedComponent = await import(`@/components/customViews/${component.path}`);
              loadedComponents[component.type] = loadedComponent.default;
            } catch (error) {
              showSnackbar({
                severity: 'error',
                message:
                  error instanceof Error
                    ? error.message
                    : `Impossible de charger le composant ${component.type}`,
              });
              loadedComponents[component.type] = () => (
                <div>Error loading component: {component.type}</div>
              );
            }
          }),
        );
      }

      setCustomComponentsMap(loadedComponents);
    };

    loadComponents();
  }, [customComponents, showSnackbar]);

  return (
    <GlobalContext.Provider
      value={{
        appProps: { customComponents, ...appProps },
        customComponentsMap,
        showAlert,
        showSnackbar,
      }}
    >
      {children}

      <Snackbar
        anchorOrigin={snackbarProps?.anchorOrigin ?? DEFAULT_SNACKBAR_POSITION}
        autoHideDuration={snackbarProps?.autoHideDuration}
        open={snackbarOpen}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          handleSnackbarClose();
        }}
      >
        <Alert
          severity={snackbarProps?.severity ?? DEFAULT_SEVERITY}
          sx={{ whiteSpace: 'pre-line' }}
          variant="filled"
          onClose={handleSnackbarClose}
        >
          {snackbarProps?.message ?? ''}
        </Alert>
      </Snackbar>

      {alertOpen ? (
        <Box
          sx={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            zIndex: (theme) => theme.zIndex.snackbar,
            transform: 'translateX(-50%)',
          }}
        >
          <Alert severity="info" variant="filled" onClose={handleAlertClose}>
            {alertMessage}
          </Alert>
        </Box>
      ) : null}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => useContext(GlobalContext);
